'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { EMPTY_OBJECT } from '#pplx/frontend/common/defaults.js';
import { FileDropScopeContext, getFileDropTarget, handledFileDropEvents, registerFileDropTarget, } from '#pplx/frontend/common/fileDropTargets.js';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, } from 'react';
const INITIAL_DROP_STATE = Object.freeze({
    isDraggingFile: false,
    hasInvalidFiles: false,
});
const EMPTY_OVERLAY_CONTEXT_VALUE = Object.freeze({ overlay: null });
// eslint-disable-next-line react/only-export-components -- The provider and consumers share this module-scoped context.
export const FileDropOverlayContext = createContext(null);
FileDropOverlayContext.displayName = 'FileDropOverlayContext';
function isFileDrag(dataTransfer) {
    return dataTransfer?.types.includes('Files') === true;
}
function hasInvalidFiles(items, validate) {
    return items.length > 0 && validate !== undefined && !validate(items);
}
/** Routes global file drags to the topmost active Composer drop capability. */
export function FileDropCapabilityHost({ children, plugins, disabled = false, }) {
    const dropPlugins = plugins.filter((plugin) => plugin.fileDrop !== undefined);
    if (dropPlugins.length > 1) {
        throw new Error(`<Composer> may mount only one 'fileDrop' capability; received ${dropPlugins.length}.`);
    }
    const plugin = dropPlugins[0];
    const contribution = plugin?.fileDrop;
    const pluginProps = plugin?.props ?? EMPTY_OBJECT;
    const config = contribution?.configure(pluginProps);
    const state = useDocumentFileDrop(config, disabled);
    // Always retain the provider around ComposerContent so adding or removing a live capability
    // cannot remount the editor subtree. The context object also distinguishes a rendered null
    // overlay from a missing provider, letting useFileDropOverlay() report composition mistakes.
    const overlayContextValue = useMemo(() => contribution === undefined || disabled || config?.disabled === true
        ? EMPTY_OVERLAY_CONTEXT_VALUE
        : { overlay: contribution.renderOverlay(pluginProps, state) }, [config?.disabled, contribution, disabled, pluginProps, state]);
    return (_jsx(FileDropOverlayContext.Provider, { value: overlayContextValue, children: _jsx(FileDropScopeContext.Provider, { value: undefined, children: children }) }));
}
function useDocumentFileDrop(config, hostDisabled) {
    const scope = useContext(FileDropScopeContext);
    const hasCapability = config !== undefined;
    const disabled = hostDisabled || !hasCapability || config.disabled === true;
    // Document listeners stay installed across live plugin-prop updates, so read the latest
    // validation and drop callbacks through a ref instead of closing over stale configuration.
    const configRef = useRef(config);
    configRef.current = config;
    const instanceId = useRef(Symbol('composer-file-drop'));
    const documentDragDepth = useRef(0);
    const [state, setState] = useState(INITIAL_DROP_STATE);
    const resetState = useCallback(() => {
        documentDragDepth.current = 0;
        setState(INITIAL_DROP_STATE);
    }, []);
    const setActiveState = useCallback((items) => {
        const nextHasInvalidFiles = hasInvalidFiles(items, configRef.current?.validate);
        setState((current) => {
            if (current.isDraggingFile && current.hasInvalidFiles === nextHasInvalidFiles) {
                return current;
            }
            return {
                isDraggingFile: true,
                hasInvalidFiles: nextHasInvalidFiles,
            };
        });
    }, []);
    // Disabling a capability during an active drag must immediately hide its overlay.
    useEffect(() => {
        if (disabled) {
            resetState();
        }
    }, [disabled, resetState]);
    // Configured hosts always suppress native file drops. Only enabled hosts participate in
    // arbitration and update drag state; cleanup removes their listeners and arbitration entry.
    useEffect(() => {
        if (!hasCapability) {
            return;
        }
        const id = instanceId.current;
        const unregister = disabled
            ? undefined
            : registerFileDropTarget({ id, scope, resetState, filesOnly: true });
        const isEligibleTarget = (event) => !disabled && getFileDropTarget(event.target) === id;
        const updateDragState = (event) => {
            const dataTransfer = event.dataTransfer;
            if (!isEligibleTarget(event) || !isFileDrag(dataTransfer)) {
                return;
            }
            // Mark the document as a valid drop target instead of letting the browser reject the drop.
            event.preventDefault();
            setActiveState(dataTransfer.items);
        };
        const handleDocumentDragEnter = (event) => {
            if (!isEligibleTarget(event) || !isFileDrag(event.dataTransfer)) {
                return;
            }
            documentDragDepth.current += 1;
            updateDragState(event);
        };
        const handleDocumentDragOver = (event) => {
            const dataTransfer = event.dataTransfer;
            if (!isFileDrag(dataTransfer)) {
                return;
            }
            // Keep the browser from opening a local file even while the capability is disabled.
            event.preventDefault();
            if (!isEligibleTarget(event)) {
                resetState();
                return;
            }
            documentDragDepth.current = Math.max(documentDragDepth.current, 1);
            setActiveState(dataTransfer.items);
        };
        const handleDocumentDragLeave = () => {
            if (documentDragDepth.current === 0) {
                return;
            }
            documentDragDepth.current -= 1;
            if (documentDragDepth.current === 0) {
                setState(INITIAL_DROP_STATE);
            }
        };
        const handleDocumentDrop = (event) => {
            const dataTransfer = event.dataTransfer;
            resetState();
            if (!isFileDrag(dataTransfer)) {
                return;
            }
            // Prevent the browser from navigating to or opening the dropped file.
            event.preventDefault();
            if (!isEligibleTarget(event) || handledFileDropEvents.has(event)) {
                return;
            }
            if (dataTransfer.items.length > 0) {
                handledFileDropEvents.add(event);
                configRef.current?.onDrop(dataTransfer.items);
            }
        };
        document.addEventListener('dragenter', handleDocumentDragEnter);
        document.addEventListener('dragover', handleDocumentDragOver);
        document.addEventListener('dragleave', handleDocumentDragLeave);
        document.addEventListener('drop', handleDocumentDrop);
        return () => {
            documentDragDepth.current = 0;
            unregister?.();
            document.removeEventListener('dragenter', handleDocumentDragEnter);
            document.removeEventListener('dragover', handleDocumentDragOver);
            document.removeEventListener('dragleave', handleDocumentDragLeave);
            document.removeEventListener('drop', handleDocumentDrop);
        };
    }, [disabled, hasCapability, resetState, scope, setActiveState]);
    return state;
}
//# sourceMappingURL=FileDropCapabilityHost.js.map